// Small pickers that open beside whatever was clicked - the task list's row
// (tasks_outline_controller.js) and the task pane (task_pane_controller.js),
// on every view, use the same ones:
//
//   openDatePopover    due date, Today/Tomorrow/Next week, reminder, repeat
//   openAssignPopover  one click adds or removes someone; search past six
//   openPickPopover    search-and-pick: the pane's tags and company
//
// Each takes an `anchor` (and a `fallback`, used when the anchor is hidden -
// a hover icon once the pointer has left the row, or on a touch screen) and
// hands its result to a callback; the caller does the write. One popover is
// open at a time. On a phone they open as a sheet along the bottom (CSS,
// .tasks-popover.is-sheet).

let current = null

export function closeTaskPopover() {
    if (!current) return
    current.cleanup()
    current.panel.remove()
    current = null
}

function openShell(kind, label, anchor, fallback) {
    closeTaskPopover()
    const panel = document.createElement("div")
    panel.className = `network-toolbar-menu-panel tasks-popover tasks-popover--${kind}`
    panel.setAttribute("role", "dialog")
    panel.setAttribute("aria-label", label)
    document.body.appendChild(panel)
    const onOutside = (ev) => { if (!panel.contains(ev.target)) closeTaskPopover() }
    const onKey = (ev) => {
        if (ev.key !== "Escape") return
        ev.preventDefault()
        ev.stopPropagation()
        closeTaskPopover()
    }
    const onResize = () => place()
    document.addEventListener("mousedown", onOutside, true)
    document.addEventListener("keydown", onKey, true)
    window.addEventListener("resize", onResize)
    const place = () => placePanel(panel, anchor, fallback)
    current = {
        kind, panel, place,
        cleanup: () => {
            document.removeEventListener("mousedown", onOutside, true)
            document.removeEventListener("keydown", onKey, true)
            window.removeEventListener("resize", onResize)
        }
    }
    return current
}

// Below the anchor, or above it when there is no room; inside the window
// either way. A zero-size anchor (display: none) gives way to the fallback.
function placePanel(panel, anchor, fallback) {
    const sheet = window.matchMedia("(max-width: 575.98px)").matches
    panel.classList.toggle("is-sheet", sheet)
    if (sheet) {
        panel.style.top = ""
        panel.style.left = ""
        return
    }
    const box = (el) => (el?.isConnected ? el.getBoundingClientRect() : null)
    const anchorRect = box(anchor)
    const rect = anchorRect && (anchorRect.width || anchorRect.height) ? anchorRect : box(fallback)
    if (!rect) return
    const width = panel.offsetWidth
    const height = panel.offsetHeight
    let top = rect.bottom + 6
    if (top + height > window.innerHeight - 8) top = Math.max(8, rect.top - height - 6)
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8))
    panel.style.top = `${Math.round(top)}px`
    panel.style.left = `${Math.round(left)}px`
}

export function isoDate(d) {
    const pad = (n) => String(n).padStart(2, "0")
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

function localInstant(dateStr, time) {
    const [y, mo, d] = dateStr.split("-").map(Number)
    const [h, mi] = time.split(":").map(Number)
    return new Date(y, mo - 1, d, h, mi).toISOString()
}

// "HH:MM" of a reminder instant in this browser's zone, or "".
export function remindTimeOf(iso) {
    const at = iso ? new Date(iso) : null
    if (!at || Number.isNaN(at.getTime())) return ""
    return `${String(at.getHours()).padStart(2, "0")}:${String(at.getMinutes()).padStart(2, "0")}`
}

const escapeHtml = (text) => String(text ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "\"": "&quot;", "'": "&#39;" }[c]))

// --- Date: due date, reminder, repeat -----------------------------------
//
// values: { dueDate: "YYYY-MM-DD"|"", remindTime: "HH:MM"|"", recurrenceKind,
// recurrenceWeekday, recurrenceInterval }. onSave(formData) gets due_date,
// the recurrence fields and - only when the time changed - remind_at.
export function openDatePopover({ anchor, fallback, values, onSave }) {
    const { panel, place } = openShell("date", "Due date", anchor, fallback)
    const weekdays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]
    panel.innerHTML = `
        <div class="tasks-popover-quick">
            <button type="button" class="tasks-popover-chip" data-days="0">Today</button>
            <button type="button" class="tasks-popover-chip" data-days="1">Tomorrow</button>
            <button type="button" class="tasks-popover-chip" data-days="7">Next week</button>
        </div>
        <label class="tasks-popover-field">
            <span><i class="bi bi-calendar-event" aria-hidden="true"></i> Date</span>
            <input type="date" class="form-control form-control-sm" data-popover-date>
        </label>
        <div class="tasks-popover-field">
            <label for="tasksPopoverRemind"><i class="bi bi-alarm" aria-hidden="true"></i> Remind me at</label>
            <div class="tasks-popover-remind-row">
                <input type="time" id="tasksPopoverRemind" class="form-control form-control-sm" data-popover-remind>
                <button type="button" class="tasks-popover-x" aria-label="Clear the reminder" title="Clear the reminder" data-popover-remind-clear><i class="bi bi-x-lg" aria-hidden="true"></i></button>
            </div>
        </div>
        <label class="tasks-popover-field">
            <span><i class="bi bi-arrow-repeat" aria-hidden="true"></i> Repeat</span>
            <select class="form-select form-select-sm" data-popover-recur>
                <option value="">Doesn't repeat</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="every_n_days">Every N days</option>
            </select>
        </label>
        <div class="tasks-popover-recur-extra">
            <select class="form-select form-select-sm" data-popover-weekday aria-label="Weekday" hidden>
                ${weekdays.map((name, i) => `<option value="${i}">${name}</option>`).join("")}
            </select>
            <input type="number" min="1" max="365" class="form-control form-control-sm" data-popover-interval aria-label="Interval in days" hidden>
        </div>
        <div class="tasks-popover-footer">
            <button type="button" class="btn btn-sm btn-link tasks-popover-clear" data-popover-clear>Remove date</button>
            <button type="button" class="btn btn-sm btn-primary" data-popover-done>Done</button>
        </div>`
    const date = panel.querySelector("[data-popover-date]")
    const remind = panel.querySelector("[data-popover-remind]")
    const recur = panel.querySelector("[data-popover-recur]")
    const weekday = panel.querySelector("[data-popover-weekday]")
    const interval = panel.querySelector("[data-popover-interval]")
    date.value = values.dueDate || ""
    remind.value = values.remindTime || ""
    recur.value = values.recurrenceKind || ""
    weekday.value = values.recurrenceWeekday !== "" && values.recurrenceWeekday != null ? String(values.recurrenceWeekday) : String(new Date().getDay())
    interval.value = values.recurrenceInterval || "2"
    const syncRecur = () => {
        weekday.hidden = recur.value !== "weekly"
        interval.hidden = recur.value !== "every_n_days"
    }
    syncRecur()
    recur.addEventListener("change", syncRecur)
    panel.querySelector("[data-popover-clear]").hidden = !date.value

    const save = () => {
        const fd = new FormData()
        const time = remind.value
        // Only a CHANGED time is sent: an untouched reminder keeps its own day
        // (the server moves it along with a new due date). A new time needs a
        // day: the due date, or - with none - today, or tomorrow once that
        // time has passed (which then becomes the due date).
        if (time !== (values.remindTime || "")) {
            if (time && !date.value) {
                const now = new Date()
                const [h, m] = time.split(":").map(Number)
                const day = new Date(now.getFullYear(), now.getMonth(), now.getDate() + (h * 60 + m <= now.getHours() * 60 + now.getMinutes() ? 1 : 0))
                date.value = isoDate(day)
            }
            fd.set("remind_at", time ? localInstant(date.value, time) : "")
        }
        fd.set("due_date", date.value || "")
        fd.set("recurrence_kind", recur.value || "")
        fd.set("recurrence_weekday", weekday.value || "")
        fd.set("recurrence_interval", interval.value || "")
        closeTaskPopover()
        onSave(fd)
    }
    // A quick date is the whole decision: it applies and closes.
    panel.querySelectorAll("[data-days]").forEach((chip) => chip.addEventListener("click", () => {
        const d = new Date()
        d.setDate(d.getDate() + parseInt(chip.dataset.days, 10))
        date.value = isoDate(d)
        save()
    }))
    panel.querySelector("[data-popover-clear]").addEventListener("click", () => { date.value = ""; save() })
    panel.querySelector("[data-popover-remind-clear]").addEventListener("click", () => { remind.value = "" })
    panel.querySelector("[data-popover-done]").addEventListener("click", save)
    panel.addEventListener("keydown", (ev) => {
        if (ev.key === "Enter" && ev.target.matches("input")) {
            ev.preventDefault()
            save()
        }
    })
    place()
    date.focus({ preventScroll: true })
}

// --- Assign: one click adds or removes someone --------------------------
//
// candidates: [{ token, label, avatar_url, initials, hue }]; selected: tokens.
// onToggle(nextTokens) does the write and resolves to the tokens now saved
// (or null on failure); chipFor(candidate) builds the avatar element.
export function openAssignPopover({ anchor, fallback, candidates, selected, onToggle, chipFor }) {
    const { panel, place } = openShell("assign", "Assign", anchor, fallback)
    let tokens = [...selected]
    panel.innerHTML = `
        <input type="search" class="tasks-tag-query" placeholder="Search people" aria-label="Search people" data-popover-query>
        <div class="tasks-popover-people" role="listbox" aria-multiselectable="true" aria-label="Assignees" data-popover-people></div>`
    const query = panel.querySelector("[data-popover-query]")
    const list = panel.querySelector("[data-popover-people]")
    // A handful of people is quicker to read than to search.
    query.hidden = candidates.length <= 6
    const render = () => {
        const needle = query.value.trim().toLowerCase()
        const matches = candidates.filter((c) => !needle || String(c.label).toLowerCase().includes(needle))
        list.innerHTML = ""
        if (!matches.length) {
            list.innerHTML = "<p class=\"tasks-popover-empty\">Nobody by that name on this list.</p>"
            return
        }
        matches.forEach((candidate) => {
            const on = tokens.includes(candidate.token)
            const btn = document.createElement("button")
            btn.type = "button"
            btn.className = `task-options-assignee${on ? " is-selected" : ""}`
            btn.dataset.token = candidate.token
            btn.setAttribute("role", "option")
            btn.setAttribute("aria-selected", String(on))
            btn.appendChild(chipFor(candidate))
            const label = document.createElement("span")
            label.className = "task-options-assignee-label"
            label.textContent = candidate.label
            btn.appendChild(label)
            const check = document.createElement("i")
            check.className = "bi bi-check2 task-options-assignee-check"
            check.setAttribute("aria-hidden", "true")
            btn.appendChild(check)
            btn.addEventListener("click", async () => {
                const prev = tokens
                tokens = prev.includes(candidate.token) ? prev.filter((t) => t !== candidate.token) : [...prev, candidate.token]
                render()
                const saved = await onToggle(tokens, prev)
                tokens = saved || prev
                if (current?.panel === panel) render()
            })
            list.appendChild(btn)
        })
    }
    query.addEventListener("input", render)
    query.addEventListener("keydown", (ev) => {
        if (ev.key !== "Enter") return
        ev.preventDefault()
        list.querySelector("[data-token]")?.click()
    })
    render()
    place()
    if (!query.hidden) query.focus({ preventScroll: true })
}

// --- Pick: search, then pick or toggle ----------------------------------
//
// search(query) resolves to [{ key, label, meta?, selected? }]; onPick(item)
// does the write and resolves to true to keep the popover open (tags toggle
// one after another), false to close it (a company is one pick).
// create(query), when given, adds a "Create …" row for a query no item matches.
export function openPickPopover({ anchor, fallback, label, placeholder, search, onPick, create }) {
    const { panel, place } = openShell("pick", label, anchor, fallback)
    panel.innerHTML = `
        <input type="search" class="tasks-tag-query" placeholder="${escapeHtml(placeholder)}" aria-label="${escapeHtml(placeholder)}" data-popover-query>
        <div class="tasks-popover-people" role="listbox" aria-label="${escapeHtml(label)}" data-popover-items></div>`
    const query = panel.querySelector("[data-popover-query]")
    const list = panel.querySelector("[data-popover-items]")
    let seq = 0
    const run = async () => {
        const mine = ++seq
        const q = query.value.trim()
        const items = await search(q).catch(() => [])
        if (mine !== seq || current?.panel !== panel) return
        const rows = [...items]
        if (create && q && !items.some((i) => String(i.label).toLowerCase() === q.toLowerCase())) rows.push({ key: `create:${q}`, label: `Create "${q}"`, create: q })
        list.innerHTML = ""
        if (!rows.length) {
            list.innerHTML = `<p class="tasks-popover-empty">${q ? "Nothing matches." : "Type to search."}</p>`
            return
        }
        rows.forEach((item) => {
            const btn = document.createElement("button")
            btn.type = "button"
            btn.className = `task-options-assignee${item.selected ? " is-selected" : ""}`
            btn.dataset.key = item.key
            btn.setAttribute("role", "option")
            btn.setAttribute("aria-selected", String(Boolean(item.selected)))
            btn.innerHTML = `<span class="task-options-assignee-label">${escapeHtml(item.label)}${item.meta ? `<small class="tasks-popover-meta">${escapeHtml(item.meta)}</small>` : ""}</span><i class="bi bi-check2 task-options-assignee-check" aria-hidden="true"></i>`
            btn.addEventListener("click", async () => {
                const keepOpen = item.create ? await create(item.create) : await onPick(item)
                if (keepOpen && current?.panel === panel) run()
                else if (current?.panel === panel) closeTaskPopover()
            })
            list.appendChild(btn)
        })
    }
    let timer = null
    query.addEventListener("input", () => { clearTimeout(timer); timer = setTimeout(run, 150) })
    query.addEventListener("keydown", (ev) => {
        if (ev.key !== "Enter") return
        ev.preventDefault()
        list.querySelector("[data-key]")?.click()
    })
    run()
    place()
    query.focus({ preventScroll: true })
}
